import {
    faCamera,
    faMicrophone,
    faPlay,
    faVideo,
    faStop,
    faRotateRight
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import styled from 'styled-components';
import { LiveTranscriptionEvent, createClient } from '@deepgram/sdk';
import { LiveTranscriptionEvents, LiveConnectionState } from '@deepgram/sdk';
import { USER_INPUT_TYPE } from '@nexxt/common/types';
import { isIOS, isSafari } from 'react-device-detect';
import axios from 'axios';
import {
    DEEPGRAM_TRANSCRIPTION_LANGUAGES,
    GOOGLE_TRANSCRIPTION_LANGUAGES
} from '@nexxt/common/constants';

const getGoogleSpeechCode = (langValue) => {
    const lang = GOOGLE_TRANSCRIPTION_LANGUAGES.find(
        (l) => l.value === langValue
    );
    return lang ? lang.googleSpeechCode : null;
};

interface VoicePromptProps {
    userText: string;
    setUserText: (text: string) => void;
    isText?: boolean;
    isAudio?: boolean;
    isVideo?: boolean;
    isCamera?: boolean;
    isDualCamera?: boolean;
    onPermissionChange?: (type: string, hasPermission: boolean) => void;
    audioPermission?: boolean;
    videoPermission?: boolean;
    setLoading: (value: boolean) => void;
    uploading: boolean;
    uploadFiles: (
        file: unknown[],
        uploadType: USER_INPUT_TYPE,
        thumbnail?: any
    ) => void; // temp file type string
    addTranscriptText: (text: string) => void;
    startImmediateAudioRecording?: boolean;
    currentPreviewFile: string;
    exitPreviewMode: () => void;
    error: boolean;
    clearError: () => void;
    langId: string;
    updateUploadingStatus: (value: boolean) => void;
}

interface liveVideoProps {
    isBackCamera?: boolean;
}

const VideoContainer = styled.div`
    position: fixed;
    right: 20;
    top: 20;
    z-index: 100;

    .live-player {
        height: 100%;
        width: 100%;
        object-fit: cover;
        ${(props: liveVideoProps) =>
            props.isBackCamera
                ? ''
                : '-webkit-transform: scaleX(-1); transform: scaleX(-1);'}
    }
`;

function normalizeLanguageCode(languageCode) {
    const languageMap = {
        zh_CN: 'zh-CN',
        zh_TW: 'zh-TW'
    };
    return languageMap[languageCode] || languageCode;
}


export function VoicePrompt({
    userText,
    setUserText,
    isText,
    isAudio = false,
    isVideo = false,
    isCamera = false,
    isDualCamera = false,
    onPermissionChange,
    audioPermission = false,
    videoPermission = false,
    setLoading,
    uploading,
    uploadFiles,
    addTranscriptText,
    startImmediateAudioRecording,
    currentPreviewFile,
    exitPreviewMode,
    error,
    clearError,
    langId,
    updateUploadingStatus
}: VoicePromptProps) {
    const videoMediaRecorder = React.useRef(null);
    const audioMediaRecorder = React.useRef(null);
    const newAudioMediaRecorder = React.useRef(null);
    const liveVideoFeed = React.useRef<any>(null);
    const [isRecording, setIsRecording] = React.useState(false);
    const [chunks, setChunks] = React.useState([]);
    const [savedMedia, setSavedMedia] = React.useState(null);
    const mimeType = isVideo ? 'video/mp4' : 'audio/mp4';
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [timer, setTimer] = React.useState(0);
    const [mediaDuration, setMediaDuration] = React.useState(0);
    const imageCanvas = React.useRef(null);
    const [capturedImage, setCapturedImage] = React.useState(null);
    const [showCapturedImaged, setShowCapturedImage] = React.useState(false);
    const [defaultWidth, setDefaultWidth] = React.useState(150);
    const [defaultHeight, setDefaultHeight] = React.useState(150);
    const [mediaWidth, setMediaWidth] = React.useState(defaultWidth);
    const [mediaHeight, setMediaHeight] = React.useState(defaultHeight);
    const [cameraDirection, setCameraDirection] = React.useState('environment');
    const [facingModes, setFacingModes] = React.useState([]);
    const [isTranscriptionLoading, setIsTranscriptionLoading] =
        React.useState(false);
    const [transcriptionResultsEvents, setTranscriptionResultsEvents] =
        React.useState([]);
    const [
        processedTranscriptionResultsIdxs,
        setProcessedTranscriptionResultsIdxs
    ] = React.useState([]);
    const [deepgramConnection, setDeepgramConnection] = React.useState(null);
    const [deepgramStatus, setDeepgramStatus] =
        React.useState<LiveConnectionState>(LiveConnectionState.CLOSED);
    const deepgramKeepAlive = React.useRef<any>();
    const [isError, setIsError] = React.useState(false);
    const [isFileAvailable, setIsFileAvailable] = React.useState(false);
    const [hasRecorded, setHasRecorded] = React.useState(false);
    const [isVideoReady, setIsVideoReady] = React.useState(true);
    const [useGcpTranscription, setUseGcpTranscription] = React.useState(false);
    const [googleGcpTranscription, setgoogleGcpTranscription] =
        React.useState('');

    const normalizedLanguageCode = normalizeLanguageCode(langId);
    //console.log("Normalized language code ",normalizedLanguageCode);
    // Check if the detected language is supported, otherwise default to English
    const languageToUse = React.useMemo(() => {
        return DEEPGRAM_TRANSCRIPTION_LANGUAGES.includes(normalizedLanguageCode)
            ? normalizedLanguageCode
            : langId;
    }, [normalizedLanguageCode]);

    //console.log("Language to use ",languageToUse);


    const [audioChunks, setAudioChunks] = React.useState([]);
    const [videoChunks, setVideoChunks] = React.useState([]);

    const connectToDeepgram = async () => {
        try {
            // Close any existing Deepgram connection
            if (deepgramConnection) {
                deepgramConnection.close();
                setDeepgramStatus(LiveConnectionState.CLOSED);
            }

            //console.log(`Transcribing using Language: ${languageToUse}`);
            const deepgram = createClient(process.env.DEEPGRAM_API_KEY);

            const deepgramLive = deepgram.listen.live({
                model: 'nova-2',
                smart_format: true,
                language: languageToUse
            });

            deepgramLive.addListener(LiveTranscriptionEvents.Open, () => {
                console.log('deepgram connection open');
                setDeepgramStatus(LiveConnectionState.OPEN);

                deepgramLive.addListener(LiveTranscriptionEvents.Close, () => {
                    console.log('deepgram connection close event detected!');
                    setDeepgramStatus(LiveConnectionState.CLOSED);
                    setDeepgramConnection(null);
                });

                deepgramLive.addListener(
                    LiveTranscriptionEvents.Transcript,
                    onTranscription
                );
            });

            deepgramLive.addListener(LiveTranscriptionEvents.Error, (error) => {
                console.error({ error });
                setIsError(true);
                setUseGcpTranscription(true); // Fallback to GCP transcription
                console.log('Fallback to Google Cloud Transcription enabled.');
            });

            setDeepgramConnection(deepgramLive);
        } catch (error) {
            setUseGcpTranscription(true); // Fallback to GCP transcription in case of error
            console.error(`Error connecting to deepgram: ${error}`);
        }
    };

    /*React.useEffect(() => {
        if (langId && deepgramConnection) {
            console.log(
                `Language ID updated: ${langId}. Reconnecting to Deepgram...`
            );
            //connectToDeepgram();
        }
    }, [langId]);*/

    React.useEffect(() => {
        return () => {
            if (deepgramConnection) {
                deepgramConnection.close();
                clearInterval(deepgramKeepAlive.current); // Clear keepAlive interval
                console.log('Deepgram connection closed on unmount');
            }
        };
    }, []);

    React.useEffect(() => {
        const newTranscriptionResultsEventIdxTuples = Object.entries(
            transcriptionResultsEvents
        ).filter(
            (idxEventTuple) =>
                !processedTranscriptionResultsIdxs.includes(idxEventTuple[0])
        );
        const newTranscriptionResultsEventIdxs =
            newTranscriptionResultsEventIdxTuples.map((tuple) => tuple[0]);
        const newTranscriptionResultsEvents =
            newTranscriptionResultsEventIdxTuples.map((tuple) => tuple[1]);
        setProcessedTranscriptionResultsIdxs((prevIdxs) => [
            ...prevIdxs,
            ...newTranscriptionResultsEventIdxs
        ]);

        const appendedResponseText = newTranscriptionResultsEvents
            .map((event) => event.channel.alternatives[0].transcript)
            .filter((transcript) => transcript?.length)
            .join('\n');
        if (appendedResponseText.length) {
            const newUserText = userText + ' ' + appendedResponseText;
            setUserText(newUserText.trim());
            console.log('Live Deepgram Transcript', newUserText);
            addTranscriptText(appendedResponseText);
        }
        setIsTranscriptionLoading(false);
    }, [transcriptionResultsEvents]);

    React.useEffect(() => {
        if (isTranscriptionLoading || isRecording || uploading) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [isTranscriptionLoading, isRecording, uploading]);

    const isReady =
        isCamera || isDualCamera
            ? videoPermission
            : isAudio
            ? audioPermission
            : true;

    const allowRecord =
        !isPlaying &&
        !uploading &&
        (((isVideo || isAudio) && deepgramStatus == LiveConnectionState.OPEN) ||
            isCamera);

    // add deepgram transcript listener
    const onTranscription = (data: LiveTranscriptionEvent) => {
        if (data.channel.alternatives[0].transcript !== '') {
            setIsFileAvailable(true);
            setTranscriptionResultsEvents((prevEvents) => [
                ...prevEvents,
                data
            ]);
        } else {
            setIsTranscriptionLoading(false);
        }
    };

    React.useEffect(() => {
        if (isFileAvailable && !isRecording) {
            try {
                updateUploadingStatus(true);
                if (isVideo) {
                    handleVideoUpload();
                } else {
                    handleAudioUpload();
                }
            } catch (error) {
                console.error('Error processing and uploading file:', error);
            }
        }
    }, [isFileAvailable, isRecording]);

    // Constants
    const JPEG_MIMETYPE = 'image/jpeg';

    // Helper to create a Blob and File from chunks
    const createFileFromChunks = (chunks, fileName, mimeType) => {
        const blob = new Blob(chunks, { type: mimeType });
        return new File([blob], fileName, {
            type: mimeType,
            lastModified: Date.now()
        });
    };

    // Function to upload and process file
    const uploadAndProcessFile = async (
        file,
        userInputType,
        thumbnailData = null
    ) => {
        try {
            console.log(`Starting upload for ${userInputType}...`);

            const formData = new FormData();
            formData.append('file', file);

            const url = `${process.env.INCA_SERVER_URL}/data/file/uploadAndProcess`;
            const response = await fetch(url, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Preprocessing failed');
            }

            // Get the processed file from the response as a Blob
            const processedBlob = await response.blob();

            // Convert Blob into a File object
            const processedFile = new File(
                [processedBlob],
                `processed_${file.name}`,
                { type: file.type }
            );

            console.log('Processed file:', processedFile);

            // Proceed with final file upload
            uploadFiles([processedFile], userInputType, thumbnailData);
        } catch (error) {
            console.error(
                `Preprocessing failed for ${userInputType}. Error: ${error.message}`
            );
            console.log(
                `Falling back to normal upload for ${userInputType}...`
            );
            // If preprocessing fails, fallback to normal file upload
            uploadFiles([file], userInputType, thumbnailData);
        }
    };

    // Function to handle video upload
    const handleVideoUpload = () => {
        const thumbnailData = imageCanvas.current.toDataURL(JPEG_MIMETYPE);

        setTimeout(() => {
            try {
                const videoFile = createFileFromChunks(
                    videoChunks,
                    'video_recording.mp4',
                    'video/mp4'
                );

                if (videoFile.size > 0) {
                    console.log('Video file created:', videoFile);
                    uploadAndProcessFile(
                        videoFile,
                        USER_INPUT_TYPE.VIDEO,
                        thumbnailData
                    );
                } else {
                    console.error(
                        'Generated video file is empty. Upload aborted.'
                    );
                }

                setVideoChunks([]);
            } catch (error) {
                console.error(
                    'Error processing and uploading video file:',
                    error
                );
            }
        }, 500);
    };

    // Function to handle audio upload
    const handleAudioUpload = () => {
        setTimeout(() => {
            try {
                const audioFile = createFileFromChunks(
                    audioChunks,
                    'audio_recording.mp4',
                    'audio/mp4'
                );

                if (audioFile.size > 0) {
                    console.log('Audio file created:', audioFile);
                    uploadAndProcessFile(audioFile, USER_INPUT_TYPE.AUDIO);
                } else {
                    console.error(
                        'Generated audio file is empty. Upload aborted.'
                    );
                }

                setAudioChunks([]);
            } catch (error) {
                console.error(
                    'Error processing and uploading audio file:',
                    error
                );
            }
        }, 500);
    };

    const stopMediaTracks = () => {
        videoMediaRecorder.current?.stream
            ?.getTracks()
            .forEach((track) => track.stop());

        audioMediaRecorder.current?.stream
            ?.getTracks()
            .forEach((track) => track.stop());
    };

    React.useEffect(() => {
        return () => {
            stopMediaTracks();
        };
    }, []);

    React.useEffect(() => {
        if (currentPreviewFile) {
            setSavedMedia(currentPreviewFile);
        } else {
            setSavedMedia(null);
        }

        setCapturedImage(null);
        setIsRecording(false);
        setIsPlaying(false);
        setIsError(false);
        stopMediaTracks();
        setHasRecorded(false);

        if (liveVideoFeed?.current) {
            liveVideoFeed.current.srcObject = null;
        }
        if (!videoPermission && isVideo) {
            requestVideoPermission();
        } else if (isCamera || isDualCamera) {
            requestCameraPermission();
        } else if (!audioPermission && isAudio) {
            requestAudioPermission();
        }
    }, [isAudio, isCamera, isDualCamera, isVideo, isText, currentPreviewFile]);

    React.useEffect(() => {
        if (!deepgramConnection) {
            connectToDeepgram();
        }
    }, [deepgramConnection]);

    React.useEffect(() => {
        if (!startImmediateAudioRecording || hasRecorded) return;

        if (isAudio && deepgramConnection) {
            startRecording().catch(() => {
                onPermissionChange('audio', false);
            });
        }
    }, [deepgramConnection, isAudio]);

    React.useEffect(() => {
        if (!deepgramConnection) return;

        if (deepgramStatus === LiveConnectionState.OPEN) {
            deepgramConnection.keepAlive();
            deepgramKeepAlive.current = setInterval(() => {
                deepgramConnection.keepAlive();
            }, 6000);
        } else {
            clearInterval(deepgramKeepAlive.current);
            console.log('clear keepAlive interval');
        }

        return () => {
            clearInterval(deepgramKeepAlive.current);
            console.log('cleanup keepAlive interval');
        };
    }, [deepgramStatus]);

    const requestAudioPermission = async () => {
        if ('MediaRecorder' in window) {
            try {
                await navigator.mediaDevices.getUserMedia({
                    audio: true
                });
                onPermissionChange('audio', true);
            } catch (err) {
                onPermissionChange('audio', false);
                setIsError(true);
                console.log(err.message);
            }
        } else {
            alert('The MediaRecorder is not supported in your browser.');
        }
    };

    const requestCameraPermission = async () => {
        if ('MediaRecorder' in window) {
            setLoading(true);
            const modes = [];
            const userStream = await navigator.mediaDevices
                .getUserMedia({
                    video: { facingMode: { exact: 'user' } }
                })
                .catch(() => null);
            if (userStream) {
                modes.push('user');
                userStream.getTracks().forEach((track) => track.stop());
            }

            const envStream = await navigator.mediaDevices
                .getUserMedia({
                    video: { facingMode: { exact: 'environment' } }
                })
                .catch(() => null);

            if (envStream) {
                modes.push('environment');
                envStream.getTracks().forEach((track) => track.stop());
            }
            setFacingModes(modes);

            startCamera();
        } else {
            alert('The MediaRecorder API is not supported in your browser.');
        }
    };

    const startCamera = async () => {
        try {
            stopMediaTracks();

            const videoStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: cameraDirection }
            });

            videoMediaRecorder.current = new MediaRecorder(videoStream);
            liveVideoFeed.current.srcObject = videoStream;
            liveVideoFeed.current.play();
            onPermissionChange('video', true);
        } catch (err) {
            onPermissionChange('video', false);
            console.log('Error please set permissions', err);
            setIsError(true);
        }
        setLoading(false);
    };

    const requestVideoPermission = async () => {
        console.log("Request video permission");
    
        if ('mediaDevices' in navigator) {
            try {
                // Request both video and audio streams to ensure permissions
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: true // Include audio in the request to handle both permissions together
                });
    
                console.log("Video and audio permission granted");
                onPermissionChange('video', true);
                setIsVideoReady(true);
    
                // Stop the stream once permissions are granted
                stream.getTracks().forEach(track => track.stop());
            } catch (err) {
                console.log("Permission denied:", err.message);
                onPermissionChange('video', false);
                setIsVideoReady(false);
            }
        } else {
            alert('The MediaRecorder API is not supported in your browser.');
        }
    }

    const dataURItoBlob = (dataURI) => {
        // convert base64 to raw binary data held in a string
        // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
        const byteString = atob(dataURI.split(',')[1]);

        // separate out the mime component
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to an ArrayBuffer
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
    };

    const captureImage = () => {
        const context = imageCanvas.current.getContext('2d');

        context.drawImage(liveVideoFeed.current, 0, 0, mediaWidth, mediaHeight);

        const JPEG_MIMETYPE = 'image/jpeg';
        const data = imageCanvas.current.toDataURL(JPEG_MIMETYPE);
        const blob = dataURItoBlob(data);
        setCapturedImage(data);
        uploadFiles(
            [new File([blob], 'test.jpeg', { type: JPEG_MIMETYPE })],
            USER_INPUT_TYPE.CAMERA
        );
        setShowCapturedImage(true);
        setTimeout(() => {
            setShowCapturedImage(false);
        }, 2000);
    };

    const switchCameras = () => {
        setCameraDirection((prev) =>
            prev === 'user' ? 'environment' : 'user'
        );
    };

    React.useEffect(() => {
        if (isCamera || isDualCamera) {
            startCamera();
        }
    }, [cameraDirection]);

    function blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => {
                if (typeof reader.result === 'string') {
                    // Extract the Base64 string from the Data URL
                    const base64String = reader.result.split(',')[1];
                    resolve(base64String);
                } else {
                    reject(new Error('Failed to convert Blob to Base64'));
                }
            };

            reader.onerror = () => {
                reject(new Error('Failed to read Blob'));
            };

            reader.readAsDataURL(blob);
        });
    }

    let transcriptionQueue = [];
    let isTranscribing = false;

    const handleDataAvailable = async (e, langId) => {
        if (e.data.size > 0) {
            const audioBlob = e.data;
            // Add the audio blob to the transcription queue
            transcriptionQueue.push({ audioBlob, langId });
            processTranscriptionQueue(); // Process the queue
        }
    };

    const processTranscriptionQueue = async () => {
        // If transcription is already in progress, exit the function
        if (isTranscribing || transcriptionQueue.length === 0) return;

        // Set the flag to indicate transcription is in progress
        isTranscribing = true;

        // Get the next chunk from the queue
        const { audioBlob, langId } = transcriptionQueue.shift();

        try {
            // Convert the audioBlob to base64
            const base64String = await blobToBase64(audioBlob);
            const audioData = {
                audio: base64String,
                languageCode: langId
            };

            const response = await axios.post(
                process.env.GCP_TRANSCRIPTION_LAMBDA_URI,
                audioData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json'
                    }
                }
            );

            // Handle the transcription response
            if (response.data.transcription.length) {
                setgoogleGcpTranscription((prevUserText) => {
                    const newUserText =
                        prevUserText + ' ' + response.data.transcription;
                    console.log('Google transcription: ' + newUserText);
                    setUserText(newUserText);
                    setgoogleGcpTranscription(newUserText);
                    addTranscriptText(response.data.transcription);
                    return newUserText;
                });
            }
        } catch (error) {
            console.error('Error during transcription:', error);
        } finally {
            // Set the flag to false after processing the transcription
            isTranscribing = false;

            // If there are more chunks in the queue, process the next one
            if (transcriptionQueue.length > 0) {
                processTranscriptionQueue();
            } else {
                setIsTranscriptionLoading(false);
            }
        }
    };

    const googleSpeechCode = getGoogleSpeechCode(languageToUse);
    // Helper function to queue transcription data
    const queueTranscription = (data) => {
        try {
            if (!useGcpTranscription && DEEPGRAM_TRANSCRIPTION_LANGUAGES.includes(languageToUse)) {
                console.log('Using Deepgram Transcription...', languageToUse);
                deepgramConnection.send(data); // Send audio data to Deepgram for transcription
            } else {
                console.log('Using GCP Transcription...', languageToUse);
                handleDataAvailable({ data }, languageToUse); // Use GCP transcription as fallback
            }
        } catch (error) {
            console.error('Error processing audio data for transcription:', error);
        } finally {
            setIsTranscriptionLoading(false); // Hide loading
        }
    };

    // Setting up the audio stream with a noise reduction filter
    const applyNoiseReduction = (audioTrack) => {
        // Create an audio context for processing the audio
        const audioContext = new AudioContext();
        const mediaStreamSource = audioContext.createMediaStreamSource(new MediaStream([audioTrack]));
    
        // Create a biquad low-pass filter to reduce high-frequency noise
        const lowPassFilter = audioContext.createBiquadFilter();
        lowPassFilter.type = "lowpass";
        lowPassFilter.frequency.value = 3000; // Cut-off frequency for noise reduction
    
        // Optional: Add a gain node to reduce amplitude (overall volume to suppress noise)
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0.9; // Reduce gain slightly to reduce background noise
    
        // Connect the audio track through the filter and gain node
        mediaStreamSource.connect(lowPassFilter).connect(gainNode).connect(audioContext.destination);
    
        // Create a MediaStream destination for the processed audio
        const processedAudioStream = audioContext.createMediaStreamDestination();
    
        // Connect the audio track to the processed audio stream
        mediaStreamSource.connect(processedAudioStream);
    
        // Return the processed audio stream
        return processedAudioStream.stream;
    };

    const startRecording = async () => {
        try {
            // Reset state variables for recording
            exitPreviewMode();
            setIsFileAvailable(false);
            setSavedMedia(null);
            setMediaDuration(0);
            setIsError(false);
            const localAudioChunks = [];
            setChunks(localAudioChunks);
            setHasRecorded(true);
    
            // Get user media with audio and optional video
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true, // Enable echo cancellation
                    noiseSuppression: true, // Enable noise suppression
                    sampleRate: 16000, // Sample rate (16 kHz is standard for speech)
                    channelCount: 1 // Mono audio is typically more effective for speech
                },
                video: isVideo
            });
    
            // Initialize MediaRecorder for audio
            const audioStream = new MediaStream(stream.getAudioTracks());
            audioMediaRecorder.current = new MediaRecorder(audioStream, {
                mimeType
            });
    
            // Set chunk size (2 seconds) and overlap (200ms)
            const CHUNK_SIZE_MS = 2000; // 2 seconds per chunk
            const OVERLAP_MS = 200; // 200ms overlap between chunks
    
            // Handle audio data available with overlap
            let lastChunkEnd = null;
            audioMediaRecorder.current.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    const currentChunk = event.data;
                    localAudioChunks.push(currentChunk);
    
                    // Combine with previous chunk's overlap
                    if (lastChunkEnd) {
                        const combinedChunk = new Blob([lastChunkEnd, currentChunk], { type: mimeType });
                        setChunks((prevChunks) => [...prevChunks, combinedChunk]); // Save combined chunk
                    } else {
                        setChunks([...localAudioChunks]); // Save the current chunk if no previous overlap
                    }
    
                    setIsTranscriptionLoading(true);
    
                    // Send the chunk to transcription engines live
                    try {
                        if (!useGcpTranscription && DEEPGRAM_TRANSCRIPTION_LANGUAGES.includes(languageToUse)) {
                            console.log('Sending chunk to Deepgram Transcription...', languageToUse);
                            deepgramConnection.send(currentChunk); // Send chunk to Deepgram for transcription
                        } else {
                            console.log('Sending chunk to GCP Transcription...', languageToUse);
                            handleDataAvailable(event, languageToUse); // Fallback to GCP transcription
                        }
                    } catch (error) {
                        console.error('Error processing audio data:', error);
                    } finally {
                        setIsTranscriptionLoading(false); // Hide loading state after processing
                    }
    
                    // Save last part of the current chunk for overlap with the next chunk
                    lastChunkEnd = currentChunk.slice(-OVERLAP_MS); // Retain last 200ms for the next chunk
                }
            };
    
            // Start recording audio
            audioMediaRecorder.current.start();
    
            // Chunk audio every 2 seconds, but stop after 1.8 seconds to capture overlap

            const recordChunk = () => {
                if (
                    audioMediaRecorder.current &&
                    typeof audioMediaRecorder.current.stop === 'function' &&
                    audioMediaRecorder.current.state !== 'inactive'
                ) {
                    audioMediaRecorder.current.stop();

                    setTimeout(() => {
                        if (audioMediaRecorder.current.state === 'inactive') {
                            audioMediaRecorder.current.start();
                        }
                    }, 100);
                }
            };
    
            setInterval(recordChunk, CHUNK_SIZE_MS - OVERLAP_MS); // Record chunks every 1.8 seconds to allow overlap
            setIsRecording(true);
            // Handle permission change and start video recording if enabled
            onPermissionChange('audio', true);
            if (isVideo) {
                const videoStream = new MediaStream(stream.getVideoTracks());
                liveVideoFeed.current.srcObject = videoStream;
                videoMediaRecorder.current = new MediaRecorder(stream);
                onPermissionChange('video', true);
                setIsVideoReady(true);
    
                // Handle video data available
                videoMediaRecorder.current.ondataavailable = (event) => {
                    if (event.data && event.data.size > 0) {
                        setVideoChunks((prevChunks) => [
                            ...prevChunks,
                            event.data
                        ]);
                    }
                };
    
                // Start video recording in chunks
                videoMediaRecorder.current.start();
    
                // Capture a frame from the video feed after 1 second
                setTimeout(() => {
                    const context = imageCanvas.current.getContext('2d');
                    context.drawImage(
                        liveVideoFeed.current,
                        0,
                        0,
                        liveVideoFeed.current.videoWidth,
                        liveVideoFeed.current.videoHeight
                    );
                }, 1000);
            } else {
                // Handle audio-only recording
                const audioStream = new MediaStream(stream.getAudioTracks());
                newAudioMediaRecorder.current = new MediaRecorder(audioStream, {
                    mimeType
                });
                onPermissionChange('audio', true);
                setIsRecording(true);
    
                // Handle audio data available
                newAudioMediaRecorder.current.ondataavailable = (event) => {
                    if (event.data && event.data.size > 0) {
                        setAudioChunks((prevChunks) => [
                            ...prevChunks,
                            event.data
                        ]);
                    }
                };
    
                newAudioMediaRecorder.current.start();
            }
        } catch (error) {
            setIsVideoReady(false);
            setIsError(true);
            console.error('Error starting recording:', error);
        }
    };
    
    


    /*const startRecording = async () => {
        try {
            // Reset state variables for recording
            exitPreviewMode();
            setIsFileAvailable(false);
            setSavedMedia(null);
            setMediaDuration(0);
            setIsError(false);
            const localAudioChunks = [];
            setChunks(localAudioChunks);
            setHasRecorded(true);


            // Get user media with audio and optional video
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: isVideo
            });

            // Initialize MediaRecorder for audio
            const audioStream = new MediaStream(stream.getAudioTracks());
          audioMediaRecorder.current = new MediaRecorder(audioStream, {
              mimeType
          });
            

            audioMediaRecorder.current.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    localAudioChunks.push(event.data);
                    setChunks([...localAudioChunks]);
                    setIsTranscriptionLoading(true);

                    try {
                        if (
                            !useGcpTranscription && // Only use GCP if Deepgram is not the default
                            DEEPGRAM_TRANSCRIPTION_LANGUAGES.includes(languageToUse) // Deepgram supports this language
                        ) {
                            console.log('Using Deepgram Transcription...', languageToUse);
                            deepgramConnection.send(event.data); // Send audio data to Deepgram for transcription
                        } else {
                            console.log('Using GCP Transcription...', languageToUse);
                            handleDataAvailable(event, languageToUse); // Use GCP transcription as fallback
                        }
                    } catch (error) {
                        console.error('Error processing audio data:', error);
                    } finally {
                        setIsTranscriptionLoading(false); // Hide loading
                    }
                }
            };

            // Start recording audio
            audioMediaRecorder.current.start();
            setIsRecording(true);

            const recordChunk = () => {
                if (
                    audioMediaRecorder.current &&
                    typeof audioMediaRecorder.current.stop === 'function' &&
                    audioMediaRecorder.current.state !== 'inactive'
                ) {
                    audioMediaRecorder.current.stop();

                    setTimeout(() => {
                        if (audioMediaRecorder.current.state === 'inactive') {
                            audioMediaRecorder.current.start();
                        }
                    }, 100);
                }
            };

            setInterval(recordChunk, 2000);

            onPermissionChange('audio', true);
            // Initialize video recording if video is enabled
            if (isVideo) {
                const videoStream = new MediaStream(stream.getVideoTracks());
                liveVideoFeed.current.srcObject = videoStream;
                videoMediaRecorder.current = new MediaRecorder(stream);
                onPermissionChange('video', true);
                setIsVideoReady(true);

                // Handle video data available
                videoMediaRecorder.current.ondataavailable = (event) => {
                    if (event.data && event.data.size > 0) {
                        setVideoChunks((prevChunks) => [
                            ...prevChunks,
                            event.data
                        ]);
                    }
                };

                // Start recording video in 5-second chunks
                videoMediaRecorder.current.start();

                // Capture a frame from the video feed after 1 second
                setTimeout(() => {
                    const context = imageCanvas.current.getContext('2d');
                    context.drawImage(
                        liveVideoFeed.current,
                        0,
                        0,
                        liveVideoFeed.current.videoWidth,
                        liveVideoFeed.current.videoHeight
                    );
                }, 1000);
            } else {
                // Handle audio-only recording
                const audioStream = new MediaStream(stream.getAudioTracks());
                newAudioMediaRecorder.current = new MediaRecorder(audioStream, {
                    mimeType
                });
                newAudioMediaRecorder.current = new MediaRecorder(stream);
                onPermissionChange('audio', true);
                setIsRecording(true);

                // Handle audio data available
                newAudioMediaRecorder.current.ondataavailable = (event) => {
                    if (event.data && event.data.size > 0) {
                        setAudioChunks((prevChunks) => [
                            ...prevChunks,
                            event.data
                        ]);
                    }
                };

                newAudioMediaRecorder.current.start();
            }
        } catch (error) {
            setIsVideoReady(false);
            setIsError(true);
            console.error('Error starting recording:', error);
        }
    };*/
    
    
    const stopRecording = () => {
        try {
            // Stop video feed if present
            liveVideoFeed.current.srcObject = null;

            // Safely stop audio and video MediaRecorders
            if (
                audioMediaRecorder.current &&
                audioMediaRecorder.current.state !== 'inactive'
            ) {
                audioMediaRecorder.current.stop();
            }
            if (
                videoMediaRecorder.current &&
                videoMediaRecorder.current.state !== 'inactive'
            ) {
                videoMediaRecorder.current.stop();
            }
            if (
                newAudioMediaRecorder.current &&
                newAudioMediaRecorder.current.state !== 'inactive'
            ) {
                newAudioMediaRecorder.current.stop();
            }

            // Handle media recording stop event
            const mediaRecorder = isVideo
                ? videoMediaRecorder.current
                : newAudioMediaRecorder.current || audioMediaRecorder.current;

            if (mediaRecorder) {
                mediaRecorder.onstop = () => {
                    // Create a blob file from the recorded chunks
                    const mediaBlob = new Blob(chunks, { type: mimeType });
                    // Create a playable URL from the blob file
                    const blobUrl = URL.createObjectURL(mediaBlob);
                    setSavedMedia(blobUrl);
                    setMediaDuration(timer);
                    setIsRecording(false);
                    deepgramConnection.finish();
                    setIsFileAvailable(true);
                };
            }

            // Clean up media tracks
            stopMediaTracks();
        } catch (error) {
            setIsError(true);
            console.error('Error stopping recording:', error);
        }
    };

    const handleRecordStart = () => {
        if (isCamera || isDualCamera) {
            captureImage();
        } else if (!isRecording) {
            startRecording();
        } else {
            stopRecording();
        }
    };

    const handlePlay = () => {
        setIsPlaying((prevState) => !prevState);
    };

    React.useEffect(() => {
        let interval;

        if (isRecording || isPlaying) {
            interval = setInterval(() => {
                setTimer((prevTime) => prevTime + 1);
            }, 1000);
        } else {
            clearInterval(interval);
            setTimer(0);
        }

        return () => clearInterval(interval);
    }, [isRecording, isPlaying]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes < 10 ? '0' : ''}${minutes}:${
            remainingSeconds < 10 ? '0' : ''
        }${remainingSeconds}`;
    };

    const handleOnProgressBarClick = (e) => {
        const clickedTime = Math.round(
            (e.nativeEvent.offsetX / e.currentTarget.offsetWidth) *
                mediaDuration
        );
        const mediaRecorder = isVideo
            ? videoMediaRecorder.current
            : audioMediaRecorder.current;

        if (mediaRecorder.current) {
            mediaRecorder.currentTime = clickedTime;
            setTimer(clickedTime);
        }
    };

    const updateDimensions = () => {
        setMediaHeight(liveVideoFeed.current.videoHeight || defaultHeight);
        setMediaWidth(liveVideoFeed.current.videoWidth || defaultWidth);
    };

    const displayWidth = mediaWidth * (defaultHeight / mediaHeight);

    React.useEffect(() => {
        let resizeTimer;

        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                const newWidth = window.innerWidth >= 768 ? 150 : 110;
                const newHeight = window.innerWidth >= 768 ? 150 : 110;
                setDefaultWidth(newWidth);
                setDefaultHeight(newHeight);
            }, 200);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    if (isText) return <></>;

    return (
        <>
            <VideoContainer
                style={{
                    width: displayWidth,
                    height: defaultHeight
                }}
                isBackCamera={
                    (isCamera || isDualCamera) &&
                    cameraDirection === 'environment'
                }
            >
                <video
                    ref={liveVideoFeed}
                    autoPlay
                    className="live-player"
                    onLoadedMetadata={updateDimensions}
                    playsInline
                />
            </VideoContainer>
            {isPlaying && isVideo ? (
                <VideoContainer
                    style={{
                        width: displayWidth,
                        height: defaultHeight
                    }}
                >
                    <video
                        className="live-player"
                        autoPlay
                        onEnded={handlePlay}
                        ref={videoMediaRecorder}
                        playsInline
                    >
                        <source src={savedMedia} type="video/mp4" />
                    </video>
                </VideoContainer>
            ) : null}

            {showCapturedImaged && (
                <VideoContainer
                    style={{
                        width: displayWidth,
                        height: defaultHeight
                    }}
                >
                    <img className="live-player" src={capturedImage} />
                </VideoContainer>
            )}
            <canvas
                id="canvas"
                ref={imageCanvas}
                width={mediaWidth}
                height={mediaHeight}
                style={{ display: 'none' }}
            />
            {isReady ? (
                <div className="record-wrap">
                    {/* TODO: temp to visualize latest userText and to later be editable on complete recording */}
                    {/* {userText} */}
                    <div className="icons">
                        <div
                            className={`icons record-icon ${
                                (allowRecord && !isVideo) ||
                                (isVideoReady && isVideo)
                                    ? ''
                                    : 'disabled'
                            }`}
                            onClick={
                                (allowRecord && !isVideo) ||
                                (isVideoReady && isVideo)
                                    ? handleRecordStart
                                    : undefined
                            }
                        >
                            {isRecording ? (
                                <span>{formatTime(timer)}</span>
                            ) : (
                                <FontAwesomeIcon
                                    icon={
                                        isVideo
                                            ? faVideo
                                            : isCamera || isDualCamera
                                            ? faCamera
                                            : faMicrophone
                                    }
                                />
                            )}
                        </div>
                        {savedMedia && (
                            <>
                                <div
                                    className={`icons record-icon ${
                                        isRecording ? 'disabled' : ''
                                    }`}
                                    onClick={
                                        !isRecording ? handlePlay : undefined
                                    }
                                >
                                    {isPlaying ? (
                                        <FontAwesomeIcon icon={faStop} />
                                    ) : (
                                        <FontAwesomeIcon icon={faPlay} />
                                    )}
                                </div>
                                <div
                                    style={{
                                        width: defaultWidth,
                                        background: 'none'
                                    }}
                                >
                                    {isPlaying && !isVideo && (
                                        <audio
                                            autoPlay
                                            onEnded={handlePlay}
                                            ref={audioMediaRecorder}
                                            src={savedMedia}
                                        />
                                    )}
                                    <progress
                                        id="progress"
                                        value={timer}
                                        max={mediaDuration}
                                        ref={audioMediaRecorder}
                                        style={{
                                            width: '100%',
                                            cursor: 'pointer'
                                        }}
                                        onClick={handleOnProgressBarClick}
                                    />
                                </div>
                                <p>{formatTime(timer)}</p>
                            </>
                        )}
                        {isCamera && facingModes.length > 1 && (
                            <div
                                className={`icons record-icon ${
                                    isPlaying ? 'disabled' : ''
                                }`}
                                onClick={switchCameras}
                            >
                                <FontAwesomeIcon icon={faRotateRight} />
                            </div>
                        )}
                    </div>
                    {/* {isRecording
                    ? getTranslation(t, 'PLACEHOLDER.PROMPT.RECORD_STOP')
                : getTranslation(t, 'PLACEHOLDER.PROMPT.RECORD_START')} */}
                    {/* TODO: update the nested ternaries and error handling, difficult to understand */}
                    {!savedMedia && !isError ? (
                        <span className="helper-text">
                            {isCamera || isDualCamera
                                ? 'Tap camera icon to take a photo'
                                : isRecording
                                ? 'Tap above to stop recording'
                                : !isVideoReady
                                ? 'Your browser needs microphone and camera permissions to continue.'
                                : !(isAudio && startImmediateAudioRecording)
                                ? 'Tap above to start recording'
                                : null}
                        </span>
                    ) : (
                        <span
                            className={`helper-text ${
                                isTranscriptionLoading
                                    ? 'loading'
                                    : isError
                                    ? 'danger'
                                    : ''
                            }`}
                        >
                            {/* TODO: better error handling for loading states as userText now has values sooner */}
                            {/* Camera text not required because it is not saved as savedMedia */}
                            {isTranscriptionLoading || uploading
                                ? 'Please wait while audio processes before sending.'
                                : isError || error
                                ? 'Sorry, there was an error with processing your recording! Please try again.'
                                : isAudio
                                ? `Tap on the microphone to add another recording`
                                : isVideo
                                ? 'Tap on the camera to add another recording'
                                : null}
                        </span>
                    )}
                </div>
            ) : (
                <div className="permission-request">
                    <p>
                        {`Your browser needs ${
                            isAudio
                                ? 'microphone'
                                : isVideo
                                ? 'microphone and camera'
                                : 'camera'
                        } permissions to continue.`}
                    </p>
                    <p>
                        Please check your browser settings and enable the
                        necessary permissions.
                    </p>
                    {/* <button
                        onClick={(e) => {
                            e.preventDefault();
                            if (isVideo) {
                                requestVideoPermission();
                            } else if (isCamera || isDualCamera) {
                                requestCameraPermission();
                            } else {
                                requestAudioPermission();
                            }
                        }}
                    >
                        Request Permission
                    </button> */}
                </div>
            )}
        </>
    );
}

export default VoicePrompt;
